//TO DO: add tracking data to headers such as UserAgent and App version info
import {
  Capacitor,
  CapacitorHttp,
  HttpHeaders,
  HttpOptions,
  HttpResponse,
} from "@capacitor/core";
import { getBearerToken, validateBearerToken, getPortalApiToken } from "./OAuth";
import { DeviceInfo } from "@capacitor/device";
import { ErrorCodes, ErrorInfo } from "./ErrorHelper";
import { getApiOptionsData, getApiSession } from "./ApiOptionsHelper";
import { getDeviceInfo } from "./Device";
import { ApiResponse } from "../models/api/ApiResponse";
import { setRequestedURL } from "./UrlHelper";
import { initializeAbpSession } from "../api/abpLogin";
import { appInsights } from "./AppInsights";
import { get, set } from "./Preferences";

export enum HttpMethods {
  GET,
  POST,
  PUT,
  DELETE,
}

export enum LoginType {
  Portal,
  EAMCore,
}

/**
 * Capacitor 4's iOS HTTP plugin force-casts every query parameter to String
 * (HttpRequestHandler.setUrlParams: `value as! String`), so a number, boolean or
 * null crashes the app natively - e.g. the equipment hierarchy's `parentType`.
 * Android coerces each value with String.valueOf and skips undefined keys, so
 * convert the same way on iOS: the query string then matches what Android sends.
 */
const toNativeSafeParams = (params: any) => {
  if (Capacitor.getPlatform() !== "ios" || params == null || typeof params !== "object") {
    return params;
  }
  const safe: Record<string, string | string[]> = {};
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === undefined) {
      return;
    }
    safe[key] = Array.isArray(value) ? value.map((item) => String(item)) : String(value);
  });
  return safe;
};

// Global request deduplication
const activeRequests = new Map<string, Promise<ApiResponse>>();

// Helper function to create request key
const createRequestKey = (
  httpMethod: HttpMethods,
  endpoint: string,
  data?: any,
  httpParams?: any,
  loginType: LoginType = LoginType.EAMCore,
  isIAMCheck: boolean = false
): string => {
  return JSON.stringify({
    method: httpMethod,
    endpoint,
    data,
    params: httpParams,
    loginType,
    isIAMCheck,
  });
};

// Helper function to create options-based request key
const createOptionsRequestKey = (
  httpMethod: HttpMethods,
  options: HttpOptions
): string => {
  return JSON.stringify({
    method: httpMethod,
    url: options.url,
    data: options.data,
    params: options.params,
    headers: options.headers,
  });
};

const PRE_BOOTSTRAP_CONFIG_KEY = "PreBootstrapConfig";

export const getPreBootstrapConfig = async () => {
  // Try to get from cache first
  const cachedConfig = await get(PRE_BOOTSTRAP_CONFIG_KEY);
  if (cachedConfig !== null && cachedConfig !== undefined) {
    return cachedConfig;
  }

  try {
    const response = await fetch("/preBootstrapConfig.json");
    if (!response.ok) {
      console.log("Failed to load Pre Bootstrap configuration");
      //Cache the null result to prevent repeated failed attempts
      await set(PRE_BOOTSTRAP_CONFIG_KEY, null);
      return null;
    }
    const preBootstrapConfig = await response.json();
    
    // Store successful result in cache
    await set(PRE_BOOTSTRAP_CONFIG_KEY, preBootstrapConfig);
    return preBootstrapConfig;
  } catch (error) {
    console.log("Error fetching pre bootstrap config:", error);
    // Cache the null result to prevent repeated failed attempts
    await set(PRE_BOOTSTRAP_CONFIG_KEY, null);
    return null;
  }
};

export const sendRequestWithOptions = async (
  httpMethod: HttpMethods,
  options: HttpOptions
): Promise<ApiResponse> => {
  // Create unique request key
  const requestKey = createOptionsRequestKey(httpMethod, options);

  // Check if same request is already in progress
  if (activeRequests.has(requestKey)) {
    console.log(
      "Returning existing request with options:",
      httpMethod,
      options.url
    );
    return activeRequests.get(requestKey)!;
  }

  // Create the request promise
  const requestPromise = executeRequestWithOptions(httpMethod, options);

  // Store the promise
  activeRequests.set(requestKey, requestPromise);

  try {
    const result = await requestPromise;
    return result;
  } finally {
    // Clean up after request completes (success or failure)
    activeRequests.delete(requestKey);
  }
};

const executeRequestWithOptions = async (
  httpMethod: HttpMethods,
  options: HttpOptions
): Promise<ApiResponse> => {
  try {
    //check if abp session needs revalidation
    if (globalThis.abpRevalidateSession) {
      initializeAbpSession();
      globalThis.abpRevalidateSession = false;
    }
    //verify bearer token validty before making Api
    const isValid = await verifyTokenAndSession(LoginType.EAMCore, false);
    if (!isValid) {
      console.log("Bearer token or Session license is invalid");
      throw new ErrorInfo(
        ErrorCodes.User_Validation_Failed,
        "Bearer token or Session license is invalid, try relogin"
      );
    }

    const requestOptions: HttpOptions = { ...options, params: toNativeSafeParams(options.params) };
    let httpResponse: HttpResponse;
    switch (httpMethod) {
      case HttpMethods.GET: {
        httpResponse = await CapacitorHttp.get(requestOptions);
        break;
      }
      case HttpMethods.POST: {
        httpResponse = await CapacitorHttp.post(requestOptions);
        break;
      }
      case HttpMethods.PUT: {
        httpResponse = await CapacitorHttp.put(requestOptions);
        break;
      }
      case HttpMethods.DELETE: {
        httpResponse = await CapacitorHttp.delete(requestOptions);
        break;
      }
    }
    return getApiResponse(httpResponse);
  } catch (error) {
    return getErrorResponse(error);
  }
};

export const sendRequest = async (
  httpMethod: HttpMethods,
  endpoint: string,
  data?: any,
  httpParams?: any,
  anonymous: boolean = false,
  loginType: LoginType = LoginType.EAMCore,
  isIAMCheck: boolean = false
): Promise<ApiResponse> => {
  // Create unique request key
  const requestKey = createRequestKey(
    httpMethod,
    endpoint,
    data,
    httpParams,
    loginType,
    isIAMCheck
  );

  // Check if same request is already in progress
  if (activeRequests.has(requestKey)) {
    console.log("Returning existing request for:", endpoint, httpMethod);
    return activeRequests.get(requestKey)!;
  }

  // Create the request promise
  const requestPromise = executeRequest(
    httpMethod,
    endpoint,
    data,
    httpParams,
    anonymous,
    loginType,
    isIAMCheck
  );

  // Store the promise
  activeRequests.set(requestKey, requestPromise);

  try {
    const result = await requestPromise;
    return result;
  } finally {
    // Clean up after request completes (success or failure)
    activeRequests.delete(requestKey);
  }
};

const executeRequest = async (
  httpMethod: HttpMethods,
  endpoint: string,
  data?: any,
  httpParams?: any,
  anonymous: boolean = false,
  loginType: LoginType = LoginType.EAMCore,
  isIAMCheck: boolean = false
): Promise<ApiResponse> => {
  try {
    //check if abp session needs revalidation
    if (!anonymous && globalThis.abpRevalidateSession) {
      initializeAbpSession();
      globalThis.abpRevalidateSession = false;
    }

    //verify bearer token validity before making API request if it's not an anonymous API call.
    if (!anonymous) {
      const isValid = await verifyTokenAndSession(loginType, isIAMCheck);
      if (!isValid) {
        console.log("Bearer token or Session license is invalid");
        throw new ErrorInfo(
          ErrorCodes.User_Validation_Failed,
          "Bearer token or Session license is invalid, try relogin"
        );
      }
    }

    appInsights?.trackEvent({
      name: "API Request",
      properties: { endpoint, httpMethod },
    });

    let httpResponse: HttpResponse;
    switch (httpMethod) {
      case HttpMethods.GET: {
        const options = {
          headers: await getApiRequestHeaders(loginType, isIAMCheck),
          url: await getRequestApiUrl(endpoint, loginType),
          params: toNativeSafeParams(httpParams),
        };
        httpResponse = await CapacitorHttp.get(options);
        break;
      }
      case HttpMethods.POST: {
        const options: HttpOptions = {
          headers: await getApiRequestHeaders(loginType, isIAMCheck),
          url: await getRequestApiUrl(endpoint, loginType),
          data: data,
          params: toNativeSafeParams(httpParams),
        };
        httpResponse = await CapacitorHttp.post(options);
        break;
      }
      case HttpMethods.PUT: {
        const options: HttpOptions = {
          headers: await getApiRequestHeaders(loginType, isIAMCheck),
          url: await getRequestApiUrl(endpoint, loginType),
          data: data,
          params: toNativeSafeParams(httpParams),
        };
        httpResponse = await CapacitorHttp.put(options);
        break;
      }
      case HttpMethods.DELETE: {
        const options: HttpOptions = {
          headers: await getApiRequestHeaders(loginType, isIAMCheck),
          url: await getRequestApiUrl(endpoint, loginType),
          params: toNativeSafeParams(httpParams),
        };
        httpResponse = await CapacitorHttp.delete(options);
        break;
      }
    }
    appInsights?.trackEvent({
      name: "API Response",
      properties: {
        endpoint,
        httpMethod,
        status: httpResponse.status,
      },
    });
    return getApiResponse(httpResponse);
  } catch (error) {
    appInsights?.trackException({ error: error as any, severityLevel: 3 });

    return getErrorResponse(error);
  }
};

export async function getRequestApiUrl(
  endpoint: string,
  loginType: LoginType = LoginType.EAMCore
): Promise<string> {
  //If the login type is portal, please make sure to pass the entire endpoint.
  if (loginType == LoginType.Portal) return endpoint;
  const apiSession = await getApiSession();
  const preBootstrapConfig = await getPreBootstrapConfig();
  if (apiSession.BackendApiUrl === null) {
    apiSession.BackendApiUrl = (
      await getApiOptionsData()
    ).Applications[0].BackendApiUrl;
  }
  let apiUrl = apiSession.BackendApiUrl ?? preBootstrapConfig.API_BASE_ADDRESS;
  if (endpoint !== null) {
    apiUrl += encodeURI(endpoint);
  }
  return apiUrl; //TODO: return base address or throw exception?
}

export const getApiRequestHeaders = async (loginType: LoginType = LoginType.EAMCore, isIAMCheck: boolean = false): Promise<HttpHeaders> => {
  if (loginType === LoginType.Portal && isIAMCheck) {
    // Use Portal token and Portal-specific headers
    return Promise.all([
      getPortalApiToken(), // Use your new persistent Portal API token
      getApiSession(),
      createDeviceHeader(),
    ]).then((values) => {
      const bearerToken = values[0];
      const apiSession = values[1];
      const deviceHeader = values[2];

      if (!bearerToken) {
        throw new Error('Portal API token is required but not available');
      }
      
      return {
        authorization: `Bearer ${bearerToken}`,
        "x-tw-plant": apiSession?.PlantCode ?? "",
        "x-tw-database": apiSession?.DatabaseName ?? "",
        "x-tw-licsession-id": apiSession?.LicenseSessionId ?? "",
        "content-type": "application/json",
        "x-tw-device-agent": deviceHeader,
      };
    });
  } else {
    // Use EAM token and EAM-specific headers (existing logic)
    return Promise.all([
      getBearerToken(),
      getApiSession(),
      createDeviceHeader(),
    ]).then((values) => {
      const bearerToken = values[0];
      const apiSession = values[1];
      const deviceHeader = values[2];
      return {
        authorization: `Bearer ${bearerToken}`,
        "x-tw-plant": apiSession?.PlantCode ?? "",
        "x-tw-database": apiSession?.DatabaseName ?? "",
        "x-tw-licsession-id": apiSession?.LicenseSessionId ?? "",
        "content-type": "application/json",
        "x-tw-device-agent": deviceHeader,
      };
    });
  }
};

export function encodeParam(queryParam: string): string {
  try {
    if (!!queryParam) {
      return encodeURIComponent(queryParam.replaceAll("%", "%25"));
    }
  } catch (e) {
    console.log(e);
  }
  return queryParam;
}

export function decodeParam(queryParam: string): string {
  try {
    let p = queryParam ? queryParam.replace(/\+/g, " ") : "";
    return decodeURIComponent(p);
  } catch (e) {
    console.log(e);
  }
  return queryParam;
}

const getApiResponse = (response: HttpResponse) => {
  const isIOSParsingErr = isIOSJsonParsingError(response.data);
  const isAndroidParsingErr = isAndroidJsonException(response.data);
  const isJsonParsingErr = isIOSParsingErr || isAndroidParsingErr;

  const dataStr = typeof response.data === "string" ? response.data : "";
  const isSessionErrInBody =
    !isJsonParsingErr &&
    response.status === 200 &&
    dataStr.includes("SessionError:true");

  const isErr = response.status !== 200 || isJsonParsingErr || isSessionErrInBody;

  const apiResponse: ApiResponse = {
    data: isSessionErrInBody
      ? "Your session has expired. Please re-login."
      : isErr
      ? processError(response.data)
      : response.data,
    isError: isErr,
    headers: response.headers,
    status: (response.status === 200 && isAndroidParsingErr) ? 500
          : (response.status === 200 && isIOSParsingErr) ? 0
          : (response.status === 200 && isSessionErrInBody) ? 500
          : response.status,
    url: response.url,
  };
  return apiResponse;
};

/** Detect Android CapacitorHttp JSON-parsing error (org.json.JSONException) */
export function isAndroidJsonException(value: any): boolean {
  if (!value) return false;
  const str =
    typeof value === "string"
      ? value
      : typeof value?.message === "string"
        ? value.message
        : "";
  if (str.includes("org.json.JSONException") || str.includes("JSONException")) return true;
  // Fallback: some error shapes only expose the text via toString()
  try { return value.toString().includes("JSONException"); } catch { return false; }
}

export function isIOSJsonParsingError(value: any): boolean {
  if (!value) return false;
  const str =
    typeof value === "string"
      ? value
      : typeof value?.message === "string"
        ? value.message
        : "";
  if (/couldn.t be read|isn.t in the correct format|is not in the correct format/i.test(str)) return true;
  try {
    const s = value.toString();
    return /couldn.t be read|isn.t in the correct format|is not in the correct format/i.test(s);
  } catch { return false; }
}

function parseAndroidJsonException(error: any): string | null {
  if (!isAndroidJsonException(error)) return null;

  const msg = typeof error === "string" ? error : (error?.message ?? "");
  if (!msg) return null;

  try {
    const match = msg.match(/Value (.*) of type/);
    if (match && match[1]) {
      return match[1].trim().replace(/(^"|"$)/g, "");
    }
  } catch {
    // fall through
  }
  return null;
}

function isIOSJsonException(error: any): boolean {
  return (
    typeof error?.message === "string" &&
    (error.message.includes("NSCocoaErrorDomain Code=3840") ||
      /The data couldn.t be read because it isn.t in the correct format/i.test(
        error.message
      ))
  );
}

function isAndroidNetworkException(value: any): boolean {
  if (!value) return false;
  const str = typeof value === "string" ? value : (value?.message ?? "");
  return str.includes("UnknownHostException");
}

const getErrorResponse = (error: any) => {
  const isAndroidParsingError = isAndroidJsonException(error);
  const isAndroidNetworkErr = !isAndroidParsingError && isAndroidNetworkException(error);
  const isIOSParsingError = isIOSJsonException(error);
  const errorResponse: ApiResponse = {
    data: isAndroidParsingError
      ? (() => {
          const extracted = parseAndroidJsonException(error);
          if (extracted && extracted.length > 0) {
            if (extracted.includes("SessionError:true")) {
              // Return the message directly — processError schedules
              // window.location.replace("/") for SessionTimedOut before React
              // re-renders, blanking the screen before the dialog shows.
              // Side-effects are applied inline; ApiError's onSessionExpiredOk
              // handles the redirect when the user dismisses the dialog.
              if (extracted.includes("SessionKilled") || extracted.includes("SessionKiledByUser")) {
                const killedBy = parseSessionKilledBy(extracted);
                dispatchSessionKilledEvent(killedBy);
                return `Your session has been terminated by ${killedBy}. Please re-login.`;
              }
              if (!extracted.includes("SessionTimedOut")) {
                globalThis.abpRevalidateSession = true;
                return "Your session has expired. Please try again.";
              }
              setRequestedURL(window.location.href)
                .catch((err) => console.error("Failed to save requested URL:", err));
              return "Your session has expired. Please re-login.";
            }
            return processError(extracted) ?? "Your session has expired. Please try again.";
          }
          return processError(error) ?? "Your session has expired. Please try again.";
        })()
      : isAndroidNetworkErr
      ? "Unable to connect to the server. Please check your network connection."
      : isIOSParsingError
      ? "Your session has expired. Please try again."
      : processError(error),
    isError: true,
    status: isAndroidParsingError ? 500 : 0,
  };
  return errorResponse;
};

export const SESSION_KILLED_EVENT = "sessionKilled";

export const parseSessionKilledBy = (errorData: string): string => {
  if (!errorData) return "Administrator";

  const match = errorData.match(
    /Message:Your Session has been killed by ([^.]+)/i
  );
  if (match && match[1]) {
    return match[1].trim();
  }

  return "Administrator";
};

export const dispatchSessionKilledEvent = (killedBy: string) => {
  const event = new CustomEvent(SESSION_KILLED_EVENT, {
    detail: { killedBy },
  });
  document.dispatchEvent(event);
};

export const processError = (errorData: any) => {
  //SessionError:true when abp session has expired
  if (isAndroidJsonException(errorData)) {
    return "Your session has expired. Please try again.";
  }
  if (isIOSJsonParsingError(errorData)) {
    return "Your session has expired. Please re-login.";
  }
  if (!!errorData) {
    if (errorData.MessageList && errorData.MessageList.length > 0) {
      return errorData.MessageList.map((message: any) => message.Text);
    }
    else if(Array.isArray(errorData) && errorData.length > 0 && errorData[0]?.Text){
      return errorData[0].Text;
    }
    else {
      const errorString = String(errorData);

      if (
        errorString.includes("SessionError:true") &&
        !errorString.includes("SessionTimedOut") &&
        !errorString.includes("SessionKilled") &&
        !errorString.includes("SessionKiledByUser")
      ) {
        //abp session revalidation needed
        globalThis.abpRevalidateSession = true;
        return "Your session has expired. Please try again.";
      } else if (
        errorString.includes("SessionError:true") &&
        errorString.includes("SessionTimedOut")
      ) {
        return "Your session has expired. Please re-login.";
      } else if (
        errorString.includes("SessionError:true") &&
        (errorString.includes("SessionKilled") ||
          errorString.includes("SessionKiledByUser"))
      ) {
        const killedBy = parseSessionKilledBy(errorString);
        dispatchSessionKilledEvent(killedBy);

        return `Your session has been terminated by ${killedBy}. Please re-login.`;
      } else if (
        (!!errorData.Message && errorData.Message.includes("relogin")) ||
        errorString.includes("relogin")
      ) {
        return "Your session has expired. Please re-login.";
      } else {
        return !!errorData.Message ? errorData.Message : errorString;
      }
    }
  }
};

const verifyTokenAndSession = async (loginType: LoginType = LoginType.EAMCore, isIAMCheck: boolean = false) => {
  if (loginType === LoginType.Portal && isIAMCheck) {
    // Verify Portal token
    const portalToken = await getPortalApiToken();
    if (!portalToken) {
      console.log("Portal API Token is missing, redirecting to login");
      await setRequestedURL(window.location.href);
      window.location.replace("/");
      return false;
    }
    
    // Optionally validate Portal token expiry
    try {
      const payload = JSON.parse(atob(portalToken.split('.')[1]));
      if (Date.now() > (payload.exp * 1000)) {
        console.log("Portal API token expired");
        await setRequestedURL(window.location.href);
        window.location.replace("/");
        return false;
      }
    } catch (error) {
      console.error('Portal token validation error:', error);
      return false;
    }
    
    return true;
  } else {
    // EAM token validation (existing logic)
    const isTokenValid = await validateBearerToken();
    if (!isTokenValid) {
      console.log("Bearer Token is invalid, redirecting to login");
      await setRequestedURL(window.location.href);
      window.location.replace("/");
    }
    return isTokenValid;
  }
};

const createDeviceHeader = async (): Promise<string> => {
  const deviceInfo: DeviceInfo = await getDeviceInfo();
  return `EAM Mobile/1.0 (platform=${deviceInfo?.platform}|os=${deviceInfo?.operatingSystem}|osVersion=${deviceInfo?.osVersion}|model=${deviceInfo?.model})`;
};
